import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonButtons, IonToolbar } from '@ionic/react';
import { useHistory } from 'react-router';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { InnerhivePage } from '../InnerhivePage';
import { Text } from '../components/Text';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { CareMapState } from '../caremap/CareMapState';
import { CommunityModelFactory } from '../share-caremap/CommunityModelFactory';
import { SharingAndAccessContent } from '../share-caremap/sharing-and-access/SharingAndAccessContent';
import { SharingAndAccessState } from '../share-caremap/sharing-and-access/SharingAndAccessState';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { Logger } from '../support/Logger';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { CommunityTabsProvider } from './CommunityTabsProvider';
import { CommunityNoCareMapPage } from './CommunityNoCareMapPage';
import { RemoveMeButton } from './RemoveMeButton';
import { PostsMovedNotice } from './PostsMovedNotice';
import { mediaMinWidth } from '../theme/utils';
import { SIDE_NAV_WIDTH } from '../navigation/NavConstants';

const useStyles = createUseStyles({
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    supporterCount: {
        paddingLeft: 6
    },
    // Room for the pinned invite button below the last row of the list.
    content: {
        paddingTop: 8,
        paddingBottom: 90
    },
    // IonContent's fixed slot, so it stays put while the list scrolls, with the page's
    // reading-column padding so the button lines up with the content (InnerhivePage). The tab
    // bar below already pads for the safe area.
    inviteFooter: {
        left: 0,
        right: 0,
        bottom: 0,
        paddingLeft: 'var(--ion-padding, 16px)',
        paddingRight: 'var(--ion-padding, 16px)',

        [mediaMinWidth.md]: {
            paddingLeft: '15%',
            paddingRight: '15%'
        },

        [mediaMinWidth.lg]: {
            paddingLeft: `calc(${SIDE_NAV_WIDTH}px + 20%)`,
            paddingRight: '20%'
        }
    },
    inviteBar: {
        padding: '12px 0',
        background: 'rgba(255, 255, 255, 0.95)',
        borderTop: '1px solid var(--ion-color-step-100)'
    },
    readerContent: {
        paddingTop: 8
    }
});

// The Community tab: who has access to the care recipient's care map, inline. Owners and
// co-owners manage it here; everyone else sees what they can act on (leaving).
export const CommunityPage: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    return (
        <ActiveCareMapProviderV2 careMapId={careMapId}>
            <CommunityPageWithCareMap careMapId={careMapId} />
        </ActiveCareMapProviderV2>
    );
};

const CommunityPageWithCareMap: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const careMapState = useInstance(CareMapState);
    const tabsProvider = useInstance(CommunityTabsProvider);
    const logger = useInstance(Logger);

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    // The factory loads a care map's community once per session; this page shows the latest.
    useAsyncEffect(async () => {
        try {
            await community.load();
        }
        catch (err: any) {
            // best effort: the cached list is still shown, and realtime pushes refresh it
            logger.error(err);
        }
    }, [community]);

    const careMap = careMapState.getActiveCaremap();

    if (!careMap || careMapState.isIncomplete(careMap)) {
        return <CommunityNoCareMapPage />;
    }

    const canManage = Boolean(careMap.links?.permissions);

    return (
        <InnerhivePage
            data-id="community-page"
            title="Community"
            tabs={tabsProvider.getTabs()}
        >
            <IonToolbar>
                <CareRecipientSwitcher trackingFrom="Community" />
                {!canManage && <ReaderToolbar />}
            </IonToolbar>

            {canManage
                ? <ManageAccess careMapId={careMapId} canInvite={Boolean(careMap.links?.invites)} />
                : <ReaderContent careMapId={careMapId} />
            }
        </InnerhivePage>
    );
};

const ManageAccess: FC<{ careMapId: string; canInvite: boolean; }> = ({ careMapId, canInvite }) => {
    const classes = useStyles();
    const history = useHistory();
    const sharingState = useInstance(SharingAndAccessState);

    return (
        <>
            <div className={classes.content}>
                <PostsMovedNotice careMapId={careMapId} canManage />
                <SharingAndAccessContent navigate={path => history.push(path)} />
            </div>
            {canInvite && (
                <div slot="fixed" className={classes.inviteFooter}>
                    <div className={classes.inviteBar}>
                        <IonButton expand="block" data-id="add-person-btn" onClick={() => sharingState.open('Community', { startOnAddPerson: true, inviteOnly: true })}>
                            Invite Someone
                        </IonButton>
                    </div>
                </div>
            )}
        </>
    );
};

// For a viewer or contributor: how many support the care recipient, and the way out.
const ReaderToolbar: FC = () => {
    const classes = useStyles();

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const count = community.getCollaboratorCount();

    return (
        <div className={classes.toolbar} data-type="community-reader-toolbar" data-loaded={community.isLoaded()}>
            {community.isLoaded() && count > 0 ? (
                <div className={classes.supporterCount}><Text type="detail">{`${count} Supporter${count !== 1 ? 's' : ''}`}</Text></div>
            ) : <>&nbsp;</>}
            <IonButtons slot="end">
                <RemoveMeButton />
            </IonButtons>
        </div>
    );
};

// Viewers had the community feed, so they hear where its posts went. Contributors arrived with
// shared notes and need no notice.
const ReaderContent: FC<{ careMapId: string; }> = ({ careMapId }) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);

    if (careMapState.getPrincipalRole() !== 'view-only') {
        return null;
    }

    return (
        <div className={classes.readerContent}>
            <PostsMovedNotice careMapId={careMapId} canManage={false} />
        </div>
    );
};
